import React from "react";
import { Autocomplete, Box, TextField, useTheme } from "@mui/material";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { filterQueryFields, QueryField, sortFieldsByTypeAndLabel } from "system/query/QueryInfo";

export interface QueryFieldSelectorProps {
  fields: QueryField[];

  allowedFieldTypes: string[];

  selectedFieldId: string | null;

  setSelectedFieldId?: Function;
  onSelectedFieldIdChanged?: Function;

  // Only show fields that have the query_supported flag set to true
  requireQuerySupported?: boolean;

  // Only show fields that have the agg_supported flag set to true
  requireAggSupported?: boolean;

  isInline?: boolean;

  children?: ReactNode;

  disableClearable?: boolean;

  placeholder? : string | undefined;

  typeOrder?: string[];
}

export function QueryFieldSelector(props: QueryFieldSelectorProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [inputValue, setInputValue] = useState<string>('');
  const typeOrder = props.typeOrder;

  const filteredFields = filterQueryFields( props.fields, {
    allowedFieldTypes: props.allowedFieldTypes,
    requireQuerySupported: props.requireQuerySupported,
    requireAggSupported: props.requireAggSupported
  });

  const validFields = sortFieldsByTypeAndLabel(filteredFields, typeOrder);

  return (
    <Box sx={{ display: props.isInline ? "inline-block" : "block", verticalAlign:"top", p:0, m:0 }}>
      {props.fields && validFields.length === 0 &&
        <Box sx={{p:1, fontSize:"1.1rem"}}>{ i18n.t("query-form.no-valid-fields") }</Box>
      }
      {validFields.length > 0 &&
        <Autocomplete
          sx={{minWidth: "150px"}}
          options={validFields}
          inputValue={inputValue}
          disableClearable={props.disableClearable}
          groupBy={(option) => option._social_field_category || option.type}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          value={validFields.find(field => field.id === props.selectedFieldId) || null}
          onChange={(event: any, newValue: QueryField | null) => {
            if (props.setSelectedFieldId) {
              props.setSelectedFieldId(newValue?.id || "");
            }
            if (props.onSelectedFieldIdChanged) {
              props.onSelectedFieldIdChanged(newValue?.id);
            }
          }}
          renderInput={(params) => <TextField autoComplete="off" {...params} placeholder={inputValue ? undefined : props.placeholder} />}
          renderGroup={(params) => (
            <Box key={params.key}>
              <Box sx={{ fontWeight:"bold", p:1, backgroundColor:theme.palette.background.default }}>
                {i18n.t([`query-social-field-categories.${params.group}`, params.group])}
              </Box>
              <ul>{params.children}</ul>
            </Box>
          )}
        />
      }
    </Box>
  );
}
